'use client'

import type { ProductAnalyticsPublicDto } from '@/lib/product-analytics-storage'
import type { CurrentMonthTurnoverSelection } from '@/lib/current-month-turnover'
import { MONTH_NAMES } from '@/lib/market-money'

function moneyFromKopecks(value: number): string {
  return `${Math.round(value / 100).toLocaleString('ru-RU')} ₽`
}

/** Month-end turnover forecast shown as one row under «Продажи за период», with month-to-date turnover against it. */
export function AnalyticsNow({
  products,
  selection,
}: {
  products: ProductAnalyticsPublicDto | null
  selection: CurrentMonthTurnoverSelection
}) {
  const forecast = products?.forecast.operational ?? null
  const targetMonth = forecast?.targetMonth ?? selection.month
  const monthLabel = MONTH_NAMES[Number(targetMonth.slice(5, 7)) - 1]?.toLocaleLowerCase('ru-RU') ?? 'месяц'
  const soldKopecks = selection.availability === 'unavailable' || selection.month !== targetMonth ? null : selection.netRevenueKopecks
  const progress = forecast && soldKopecks !== null && forecast.estimateKopecks > 0
    ? Math.round((soldKopecks / forecast.estimateKopecks) * 100)
    : null

  return (
    <a
      id="now"
      href="#forecasts"
      data-testid="forecast-row"
      aria-label={`Прогноз на ${monthLabel}`}
      className="analytics-anchor surface-card group mt-3 flex flex-col gap-3 px-5 py-3.5 transition hover:border-gold-deep lg:flex-row lg:items-center lg:gap-6"
    >
      <div className="flex min-w-0 flex-wrap items-baseline gap-x-2.5 gap-y-1">
        <span className="eyebrow">Прогноз на {monthLabel}</span>
        <strong className="tab-nums whitespace-nowrap text-[18px] font-semibold text-ink">{forecast ? `≈ ${moneyFromKopecks(forecast.estimateKopecks)}` : '—'}</strong>
        <span className="text-[12px] text-ink-muted">
          {forecast
            ? <>диапазон <span className="tab-nums whitespace-nowrap">{moneyFromKopecks(forecast.lowKopecks)}–{moneyFromKopecks(forecast.highKopecks)}</span> · рабочий ориентир</>
            : 'для расчёта нужен хотя бы один месяц продаж'}
        </span>
      </div>
      {progress !== null && soldKopecks !== null && (
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <div
            role="progressbar"
            aria-label="Продано от прогноза"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.min(100, progress)}
            className="h-1.5 min-w-[80px] flex-1 overflow-hidden bg-bone"
          >
            <div className="h-full bg-gold" style={{ width: `${Math.min(100, progress)}%` }} />
          </div>
          <span className="whitespace-nowrap text-[12px] text-ink-muted">
            продано <span data-testid="forecast-row-sold" className="tab-nums font-medium text-ink">{moneyFromKopecks(soldKopecks)}</span> · <span className="tab-nums">{progress}%</span>
          </span>
        </div>
      )}
      <span aria-hidden="true" className="hidden text-base text-ink-faint transition-transform group-hover:translate-x-0.5 lg:block">→</span>
    </a>
  )
}
